/**
 * The Website tab's questions, written as analytics queries, and the shaping of their answers.
 *
 * WHICH EVENTS: brand sites run two trackers at once (the site's own and the Pulsar beacon). Every figure
 * here comes from Pulsar alone, so the cards agree with each other and nothing is counted twice:
 *  - a page view is a Pulsar `page_visit` (sent on load and on every in-site navigation);
 *  - time on site is the sum of Pulsar `engagement` slices (`active_ms`, the time the page was actually
 *    in front of the visitor), divided by the visits it was measured on.
 * Visitors / visits are the analytics service's own distinct counts of the browser's anonymous ids
 * (counted per address, so the same person on the site and on a landing page counts on each).
 *
 * TIME: the analytics buckets are UTC, so the windows are UTC too (whole UTC days, ending now).
 */
import type {
  AggregateQuery,
  AnalyticsFilter,
  ConditionGroup,
  AnalyticsMetric,
  AnalyticsRow,
  AttributionUnit,
  TimeBucket,
} from './api';
import type { Selection } from './filters';
import { selectionEmpty } from './selection';

export type WebsiteInterval = 'TODAY' | 'YESTERDAY' | '7D' | '30D' | '90D';

export const WEBSITE_INTERVALS: WebsiteInterval[] = ['TODAY', 'YESTERDAY', '7D', '30D', '90D'];

/** A run of whole UTC days the owner picked, `YYYY-MM-DD..YYYY-MM-DD` (both days included). */
export type CustomRange = `${string}..${string}`;

/** The period the tab shows: a preset or a custom run of days. */
export type WebsitePeriod = WebsiteInterval | CustomRange;

/** The longest custom range (the analytics service attributes at most 90 days per question). */
export const MAX_RANGE_DAYS = 90;

export const customRange = (from: string, to: string): CustomRange => `${from}..${to}`;

export const isPreset = (period: string): period is WebsiteInterval =>
  (WEBSITE_INTERVALS as readonly string[]).includes(period);

const MS_HOUR = 60 * 60 * 1000;
const MS_DAY = 24 * MS_HOUR;

const PATH = 'properties.page.path';
const DEVICE = 'properties.device.device_type';
const SYSTEM = 'properties.device.os';
const ACTIVE_MS = 'properties.event_data.active_ms';

const PULSAR: AnalyticsFilter = { field: 'producer', op: 'eq', value: 'pulsar' };
const PAGE_VIEWS: AnalyticsFilter[] = [PULSAR, { field: 'event_type', op: 'eq', value: 'page_visit' }];
const ENGAGEMENT: AnalyticsFilter[] = [PULSAR, { field: 'event_type', op: 'eq', value: 'engagement' }];

/** How many pages the "Most visited pages" card lists. */
export const TOP_PAGES = 8;

export type Span = { from: number; to: number };

export type WebsiteWindow = {
  current: Span;
  previous: Span;
  bucket: TimeBucket;
  /** Start of every bucket in the current span, in order (empty buckets included). */
  buckets: number[];
};

const utcDayStart = (ms: number): number => {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/**
 * A custom range's first day and the day after its last (UTC ms) and its length, or null when it is not one: days in
 * order, at most {@link MAX_RANGE_DAYS}, and (given `nowMs`) none after today. Pure.
 */
export const rangeDays = (period: string, nowMs?: number): { from: number; to: number; days: number } | null => {
  const m = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/.exec(period);
  if (!m) return null;
  const from = Date.parse(`${m[1]}T00:00:00Z`);
  const last = Date.parse(`${m[2]}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(last) || last < from) return null;
  if (nowMs !== undefined && last > utcDayStart(nowMs)) return null;
  const days = Math.round((last - from) / MS_DAY) + 1;
  return days > MAX_RANGE_DAYS ? null : { from, to: last + MS_DAY, days };
};

const bucketStarts = (span: Span, step: number): number[] => {
  const out: number[] = [];
  for (let t = span.from; t < span.to; t += step) out.push(t);
  return out;
};

/** The selected period, the equal period before it (for the "vs" figures) and the chart buckets. A custom range of a
 *  day or two is charted by the hour, longer ones by the day; one that is not valid falls back to the last 7 days. */
export const websiteWindow = (interval: WebsitePeriod, nowMs: number): WebsiteWindow => {
  const day0 = utcDayStart(nowMs);
  const hourly = (current: Span, previous: Span): WebsiteWindow => ({
    current,
    previous,
    bucket: 'hour',
    buckets: bucketStarts(current, MS_HOUR),
  });
  const daily = (days: number): WebsiteWindow => {
    const current = { from: day0 - (days - 1) * MS_DAY, to: nowMs };
    const previous = { from: current.from - days * MS_DAY, to: nowMs - days * MS_DAY };
    return { current, previous, bucket: 'day', buckets: bucketStarts(current, MS_DAY) };
  };
  switch (interval) {
    case 'TODAY':
      return hourly({ from: day0, to: nowMs }, { from: day0 - MS_DAY, to: nowMs - MS_DAY });
    case 'YESTERDAY':
      return hourly({ from: day0 - MS_DAY, to: day0 }, { from: day0 - 2 * MS_DAY, to: day0 - MS_DAY });
    case '7D':
      return daily(7);
    case '30D':
      return daily(30);
    case '90D':
      return daily(90);
    default: {
      const range = rangeDays(interval, nowMs);
      if (!range) return daily(7);
      const current = { from: range.from, to: Math.min(range.to, nowMs) };
      // The same stretch before it: a range that runs into today compares with as much of the earlier one.
      const previous = { from: range.from - range.days * MS_DAY, to: current.to - range.days * MS_DAY };
      const hourly = range.days <= 2;
      return {
        current,
        previous,
        bucket: hourly ? 'hour' : 'day',
        buckets: bucketStarts(current, hourly ? MS_HOUR : MS_DAY),
      };
    }
  }
};

const iso = (ms: number): string => new Date(ms).toISOString();
const range = (span: Span): { from: string; to: string } => ({ from: iso(span.from), to: iso(span.to) });

/* ── one landing page ────────────────────────────────────────────────────────── */

/**
 * Narrows every query to the picked pages: their paths, on the addresses of their kind (a landing page on the brand's
 * `lp.` hosts, a page of the website on its own hosts), so the same path elsewhere does not count; with both kinds
 * picked, the paths count on both kinds of address. `hosts` empty: the paths on every address. A landing page's path in
 * the events is the path of its published address (Pulsar's `properties.page.path`, no query string). `exclude`: every
 * page but these (on every address).
 */
export type WebsiteScope = { paths: string[]; hosts: string[]; exclude?: boolean };

/** One page the owner picked: a landing page (on the `lp.` hosts) or a page of the website (on its own hosts). */
export type PageScope = { kind: 'landing' | 'site'; path: string };

/** The most pages one filter holds: each is asked with and without its trailing slash, and a filter takes 50 values. */
export const MAX_PAGES = 25;

/** The path of a page address ('' when it is not an absolute URL). */
export const pathOfUrl = (url: string): string => {
  try {
    return new URL(url.trim()).pathname || '/';
  } catch {
    return '';
  }
};

/** A path with and without its trailing slash, so both ways of opening the page count. */
const pathVariants = (path: string): string[] => {
  const bare = path.replace(/\/+$/, '');
  return bare === '' ? ['/'] : [bare, `${bare}/`];
};

/** The query narrowed to the picked pages, or to every page but those left out (unchanged without any). */
export const scoped = (query: AggregateQuery, scope: WebsiteScope | null): AggregateQuery => {
  if (!scope || scope.paths.length === 0) return query;
  const paths = [...new Set(scope.paths.flatMap(pathVariants))].slice(0, MAX_VALUES);
  return {
    ...query,
    ...(!scope.exclude && scope.hosts.length > 0 ? { hosts: scope.hosts } : {}),
    filters: [...(query.filters ?? []), { field: PATH, op: scope.exclude ? 'not_in' : 'in', value: paths }],
  };
};

/* ── where the visits came from ─────────────────────────────────────────────── */

/** The sources the analytics service credits a visit to (its traffic channels), in the order the picker lists them:
 *  the visits that came straight in first. */
export const CHANNELS = [
  'Direct',
  'Organic Search',
  'Paid Search',
  'Organic Social',
  'Paid Social',
  'AI',
  'Referral',
  'Email',
  'Other Paid',
  'Unknown',
] as const;

export type Channel = (typeof CHANNELS)[number];

export const isChannel = (v: string | null): v is Channel => v !== null && (CHANNELS as readonly string[]).includes(v);

/**
 * The platforms the analytics service names within a source (its source keys), as the source picker lists them. The
 * other sources name whatever brought the visit (another website's address, an email or ad's `utm_source`), so their
 * platforms are only known from the visits themselves.
 */
export const PLATFORMS: Partial<Record<Channel, readonly string[]>> = {
  'Organic Search': ['google', 'bing', 'duckduckgo', 'yahoo', 'ecosia', 'yandex'],
  'Paid Search': ['google', 'bing'],
  'Organic Social': ['meta', 'instagram', 'youtube', 'x', 'linkedin', 'reddit', 'tiktok'],
  'Paid Social': ['meta', 'tiktok'],
  AI: ['chatgpt', 'perplexity', 'gemini', 'copilot', 'claude'],
};

const PLATFORM_NAMES: Record<string, string> = {
  google: 'Google',
  bing: 'Bing',
  duckduckgo: 'DuckDuckGo',
  yahoo: 'Yahoo',
  ecosia: 'Ecosia',
  yandex: 'Yandex',
  meta: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
  x: 'X',
  linkedin: 'LinkedIn',
  reddit: 'Reddit',
  tiktok: 'TikTok',
  chatgpt: 'ChatGPT',
  perplexity: 'Perplexity',
  gemini: 'Gemini',
  copilot: 'Copilot',
  claude: 'Claude',
};

/** A platform's name (a brand name, never translated); another website's address or a `utm_source` as it is. */
export const platformName = (key: string): string => (Object.hasOwn(PLATFORM_NAMES, key) ? PLATFORM_NAMES[key] : key);

/** One filter's values: these (`in`) or every one but these (`out`). */
export type Match = { op: 'in' | 'out'; values: readonly string[] };

/** The most values one filter holds (the analytics service's limit). */
export const MAX_VALUES = 50;

export type Attribution = Selection & { campaigns?: Match | null; forceCredited?: boolean };
export const NO_ATTRIBUTION: Attribution = { channels: null, links: null };
const matchFilter = (field: string, m: Match): AnalyticsFilter => {
  const values = m.values.slice(0, MAX_VALUES);
  if (m.op === 'in')
    return values.length === 1 ? { field, op: 'eq', value: values[0] } : { field, op: 'in', value: values };
  return values.length === 1 ? { field, op: 'ne', value: values[0] } : { field, op: 'not_in', value: values };
};
const CHANNEL_FIELD = 'attribution.channel';
const PLATFORM_FIELD = 'attribution.source';
const LINK_FIELD = 'attribution.aff_id';
const ARRIVAL_LINK_FIELD = 'properties.page.url_params.aff_id';
const ARRIVAL_CHANNEL_FIELD = 'traffic.channel';
const ARRIVAL_PLATFORM_FIELD = 'traffic.source';
export const sourceHasArrivals = (attr: Attribution): boolean => !attr.channels?.some((c) => c.channel === 'Unknown');
export const creditedOnly = (attr: Attribution): boolean =>
  !!attr.forceCredited ||
  !sourceHasArrivals(attr) ||
  !!attr.links?.noLink ||
  !!attr.links?.exceptAffiliates?.length ||
  attr.campaigns?.op === 'out';
export const attributionActive = (attr: Attribution | null): attr is Attribution =>
  attr !== null && (attr.channels !== null || attr.links !== null || !!attr.campaigns);
export const countsArrivals = (attr: Attribution): boolean =>
  attributionActive(attr) && !creditedOnly(attr) && !selectionEmpty(attr);
export const withoutFacet = (attr: Attribution, facet: 'source' | 'link'): Attribution => ({
  ...attr,
  forceCredited: creditedOnly(attr),
  ...(facet === 'source' ? { channels: null } : { links: null, campaigns: null }),
});

type Term = AnalyticsFilter[];
const selectionTerms = (attr: Attribution, arrival: boolean, except?: 'source' | 'link'): Term[][] => {
  const channel = arrival ? ARRIVAL_CHANNEL_FIELD : CHANNEL_FIELD;
  const platform = arrival ? ARRIVAL_PLATFORM_FIELD : PLATFORM_FIELD;
  const link = arrival ? ARRIVAL_LINK_FIELD : LINK_FIELD;
  const groups: Term[][] = [];
  if (except !== 'source' && attr.channels !== null) {
    const whole = attr.channels.filter((c) => c.platforms === 'all' && !c.except.length).map((c) => c.channel);
    const terms: Term[] = whole.length ? [[{ field: channel, op: 'in', value: whole }]] : [];
    attr.channels
      .filter((c) => c.platforms !== 'all' || c.except.length)
      .forEach((c) => {
        if (c.platforms !== 'all' && !c.platforms.length) return;
        terms.push([
          { field: channel, op: 'eq', value: c.channel },
          {
            field: platform,
            op: c.platforms === 'all' ? 'not_in' : 'in',
            value: c.platforms === 'all' ? c.except : c.platforms,
          },
        ]);
      });
    if (terms.length) groups.push(terms);
  }
  if (except !== 'link') {
    const l = attr.links;
    const terms: Term[] = [];
    if (l?.allExcept !== null && l?.allExcept !== undefined) {
      const excluded = [...(!arrival && !l.noLink ? [''] : []), ...l.allExcept];
      if (excluded.length) terms.push([{ field: link, op: 'not_in', value: excluded }]);
      else if (arrival) terms.push([{ field: link, op: 'exists' }]);
    } else if (l) {
      l.affiliates.forEach((a) =>
        terms.push([
          { field: link, op: 'starts_with', value: `${a.id}_` },
          ...(a.exceptLinks.length ? [{ field: link, op: 'not_in' as const, value: a.exceptLinks }] : []),
        ]),
      );
      if (l.exact.length) terms.push([{ field: link, op: 'in', value: l.exact }]);
      if (l.noLink && !arrival) terms.push([{ field: LINK_FIELD, op: 'eq', value: '' }]);
    } else if (attr.campaigns) {
      if (attr.campaigns.op === 'in')
        attr.campaigns.values.forEach((id) => terms.push([{ field: link, op: 'contains', value: `_${id}_` }]));
    }
    if (terms.length) groups.push(terms);
  }
  return groups;
};

export const conditionCounts = (attr: Attribution): number[] =>
  selectionTerms(attr, false).map((terms) => terms.reduce((n, term) => n + term.length, 0));
export const selectionIssue = (attr: Attribution, conditionsAvailable = true): 'limit' | 'legacy' | null => {
  const counts = conditionCounts(attr);
  const values = [
    attr.channels?.reduce((n, c) => n + (c.platforms === 'all' ? 1 + c.except.length : c.platforms.length), 0) ?? 0,
    attr.links
      ? attr.links.affiliates.reduce((n, a) => n + 1 + a.exceptLinks.length, 0) +
        attr.links.exact.length +
        (attr.links.exceptAffiliates?.length ?? 0) +
        (attr.links.allExcept?.length ?? 0)
      : 0,
  ];
  if (
    counts.some((n) => n > 25) ||
    counts.reduce((a, b) => a + b, 0) > 50 ||
    values.some((n) => n > MAX_VALUES) ||
    (attr.links?.allExcept?.length ?? 0) + (attr.links?.noLink ? 0 : 1) > MAX_VALUES
  )
    return 'limit';
  if (!conditionsAvailable) {
    const cs = attr.channels ?? [];
    const l = attr.links;
    if (
      cs.length > 1 ||
      cs.some((c) => c.except.length || (c.platforms !== 'all' && c.platforms.length > 1)) ||
      (l &&
        (l.allExcept !== null ||
          l.affiliates.some((a) => a.exceptLinks.length) ||
          l.affiliates.length + l.exact.length + Number(l.noLink) > 1)) ||
      (attr.campaigns?.values.length ?? 0) > 1
    )
      return 'legacy';
  }
  return null;
};

export const compileSelection = (
  attr: Attribution,
  arrival = false,
  except?: 'source' | 'link',
): Pick<AggregateQuery, 'filters' | 'conditions'> => {
  const filters: AnalyticsFilter[] = [];
  const groups: ConditionGroup[] = [];
  selectionTerms(attr, arrival, except).forEach((terms) => {
    if (terms.length === 1) {
      filters.push(
        ...terms[0].map(
          (f): AnalyticsFilter =>
            f.op === 'in' && Array.isArray(f.value) && f.value.length === 1 ? { ...f, op: 'eq', value: f.value[0] } : f,
        ),
      );
      return;
    }
    const conditions: AnalyticsFilter[] = [];
    const joins: ('AND' | 'OR')[] = [];
    terms.forEach((term, i) =>
      term.forEach((f, j) => {
        if (i || j) joins.push(j ? 'AND' : 'OR');
        conditions.push(f);
      }),
    );
    groups.push({ conditions, joins });
  });
  return {
    filters,
    ...(groups.length
      ? { conditions: { groups, ...(groups.length > 1 ? { joins: groups.slice(1).map(() => 'AND' as const) } : {}) } }
      : {}),
  };
};
export const withSelection = (
  query: AggregateQuery,
  attr: Attribution,
  arrival = false,
  except?: 'source' | 'link',
): AggregateQuery => {
  const compiled = compileSelection(attr, arrival, except);
  return { ...query, ...compiled, filters: [...(query.filters ?? []), ...(compiled.filters ?? [])] };
};
export const MAX_FILTERS = 8;
export const fitsFilters = (
  query: AggregateQuery,
  attr: Attribution,
  attributing: boolean,
  scope: WebsiteScope | null,
  countries: Match | null,
  ranked = false,
): boolean => {
  const asked = inCountries(scoped(query, scope), countries);
  const parts = attributing ? attributedParts(asked, attr) : [asked];
  return (
    !selectionIssue(attr) &&
    parts.every((part, i) => (part.filters?.length ?? 0) + (ranked && i > 0 ? 1 : 0) <= MAX_FILTERS)
  );
};

/**
 * The attributed questions one query becomes. An attributed query counts one thing: visitors (`visitor`), visits
 * (`session`), or events (`event`, which cannot count visitors), so a query's metrics are split by what they count.
 * Time on site is measured on events, and only on identified visits (unidentified time would inflate the average).
 * The part that orders the answer comes first: the others are narrowed to its rows (see {@link mergeAttributed}).
 * Pure.
 */
export const attributedParts = (query: AggregateQuery, attr: Attribution): AggregateQuery[] => {
  const engagement = (query.filters ?? []).some((f) => f.field === 'event_type' && f.value === 'engagement');
  const unitOf = (m: AnalyticsMetric): AttributionUnit =>
    engagement || (m.op !== 'visitors' && m.op !== 'sessions') ? 'event' : m.op === 'visitors' ? 'visitor' : 'session';
  const byUnit = new Map<AttributionUnit, AnalyticsMetric[]>();
  query.metrics.forEach((m) => byUnit.set(unitOf(m), [...(byUnit.get(unitOf(m)) ?? []), m]));
  const orderField = query.orderBy?.field;
  const arrivals = countsArrivals(attr);
  const parts = [...byUnit.entries()]
    .filter(
      ([unit]) =>
        unit === 'event' ||
        arrivals ||
        (!query.timeBucket && !query.groupBy?.includes(PATH) && !query.filters?.some((f) => f.field === PATH)),
    )
    .map(([unit, metrics]): AggregateQuery => {
      const ordersHere =
        !orderField || !query.metrics.some((m) => m.name === orderField) || metrics.some((m) => m.name === orderField);
      const { orderBy, ...rest } = query;
      const order = orderBy && ordersHere ? { orderBy } : {};
      // People and visits through a picked link or source: its arrivals, read from the page views as recorded.
      if (arrivals && unit !== 'event') {
        return {
          ...withSelection(rest, attr, true),
          metrics,
          ...order,
        };
      }
      const credited = withSelection(rest, attr);
      return {
        ...credited,
        metrics,
        filters: [
          ...(credited.filters ?? []),
          ...(engagement ? [{ field: 'attribution.has_session', op: 'eq' as const, value: true }] : []),
        ],
        attribution: { unit },
        ...order,
      };
    });
  return parts.sort((x, y) => Number(!x.orderBy) - Number(!y.orderBy));
};

/** The key of an answer row: its groups and its time bucket. */
const rowKey = (row: AnalyticsRow, fields: string[]): string => fields.map((f) => String(row[f] ?? '')).join('\u0000');

/**
 * The narrowing a later part needs so it answers the same rows as the first (a limited, grouped query: the most visited
 * pages); null when it needs none. `ranked`: the first part's rows are the answer (a top list), so a later part is
 * always narrowed to them, not only when the first one filled its limit. Pure.
 */
export const narrowToRows = (query: AggregateQuery, first: AnalyticsRow[], ranked = false): AnalyticsFilter | null => {
  const group = query.groupBy?.length === 1 ? query.groupBy[0] : null;
  if (!group || !query.limit || first.length === 0 || (!ranked && first.length < query.limit)) return null;
  const values = [...new Set(first.map((r) => r[group]).filter((v): v is string => typeof v === 'string'))];
  return values.length > 0 ? { field: group, op: 'in', value: values } : null;
};

/** The parts' answers as one: every part's rows (the first part's first, in its order), with each other part's figures
 *  joined on. `ranked` (a top list): the first part's rows only. Pure. */
export const mergeAttributed = (query: AggregateQuery, answers: AnalyticsRow[][], ranked = false): AnalyticsRow[] => {
  const fields = [...(query.groupBy ?? []), ...(query.timeBucket ? ['time_bucket'] : [])];
  // A top list answers the first part's rows only: a page another part adds (visitors who arrived on a page none of the
  // counted views reached) is not one of its top pages.
  const merged = new Map<string, AnalyticsRow>();
  answers.forEach((rows, i) =>
    rows.forEach((row) => {
      const key = rowKey(row, fields);
      if (i > 0 && ranked && !merged.has(key)) return;
      merged.set(key, { ...(merged.get(key) ?? {}), ...row });
    }),
  );
  return [...merged.values()];
};

/** Visitors per source and platform (the source picker and the breakdown card), within the picked link. Each visitor
 *  is credited to one pair, so a source's visitors are the sum of its platforms'. */
export const sourcesQuery = (span: Span, attr: Attribution): AggregateQuery =>
  withSelection(
    {
      ...range(span),
      filters: PAGE_VIEWS,
      groupBy: [CHANNEL_FIELD, PLATFORM_FIELD],
      metrics: [{ name: 'visitors', op: 'visitors' }],
      orderBy: { field: 'visitors', direction: 'desc' },
      limit: 1000,
      attribution: { unit: 'visitor' },
    },
    attr,
    false,
    'source',
  );
export const linksQuery = (span: Span, attr: Attribution): AggregateQuery =>
  withSelection(
    {
      ...range(span),
      filters: PAGE_VIEWS,
      groupBy: [LINK_FIELD],
      metrics: [{ name: 'visitors', op: 'visitors' }],
      orderBy: { field: 'visitors', direction: 'desc' },
      limit: 1000,
      attribution: { unit: 'visitor' },
    },
    attr,
    false,
    'link',
  );
export const linkReachQuery = (span: Span, attr: Attribution): AggregateQuery =>
  withSelection(
    {
      ...range(span),
      filters: [...PAGE_VIEWS, { field: ARRIVAL_LINK_FIELD, op: 'exists' }],
      groupBy: [ARRIVAL_LINK_FIELD],
      metrics: [{ name: 'visitors', op: 'visitors' }],
      orderBy: { field: 'visitors', direction: 'desc' },
      limit: 1000,
    },
    attr,
    true,
    'link',
  );
export const sourceReachQuery = (span: Span, attr: Attribution): AggregateQuery =>
  withSelection(
    {
      ...range(span),
      filters: [...PAGE_VIEWS, { field: ARRIVAL_CHANNEL_FIELD, op: 'ne', value: 'Unknown' }],
      groupBy: [ARRIVAL_CHANNEL_FIELD, ARRIVAL_PLATFORM_FIELD],
      metrics: [{ name: 'visitors', op: 'visitors' }],
      orderBy: { field: 'visitors', direction: 'desc' },
      limit: 1000,
    },
    attr,
    true,
    'source',
  );
export const channelReachQuery = (span: Span, attr: Attribution): AggregateQuery => ({
  ...sourceReachQuery(span, attr),
  groupBy: [ARRIVAL_CHANNEL_FIELD],
  limit: 50,
});

/* ── the visitors' country ───────────────────────────────────────────────────── */

/** A visit's country: its GeoIP country (ISO 3166 alpha-2, e.g. RO, MX), null when unknown (and on visits before about
 *  September 2026). On a credited count it is the country of the person's latest page view. */
const COUNTRY_FIELD = 'geo.country_code';

/** A country as the address carries it (two letters, upper case), or null. Pure. */
export const countryCode = (v: string | null | undefined): string | null => {
  const code = (v ?? '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : null;
};

/** The query narrowed to the visits from the picked countries, or from every country but some (unchanged without
 *  any). A visit whose country is not known counts in neither. Pure. */
export const inCountries = (query: AggregateQuery, countries: Match | null): AggregateQuery =>
  countries && countries.values.length > 0
    ? { ...query, filters: [...(query.filters ?? []), matchFilter(COUNTRY_FIELD, countries)] }
    : query;

/** Visitors per country (the country picker's counts, asked like the cards, so each is what picking it shows). */
export const countriesQuery = (span: Span): AggregateQuery => ({
  ...range(span),
  filters: [...PAGE_VIEWS, { field: COUNTRY_FIELD, op: 'exists' }],
  groupBy: [COUNTRY_FIELD],
  metrics: [{ name: 'visitors', op: 'visitors' }],
  orderBy: { field: 'visitors', direction: 'desc' },
  limit: 250,
});

export type CountryRow = { code: string; visitors: number };

export const shapeCountries = (rows: AnalyticsRow[]): CountryRow[] =>
  rows
    .map((r) => ({ code: countryCode(str(r[COUNTRY_FIELD])), visitors: num(r.visitors) }))
    .filter((c): c is CountryRow => c.code !== null && c.visitors > 0);

/** The visitors, as one figure: a picker choice's count (an affiliate's, a campaign's), asked with its filters. */
export const visitorsQuery = (span: Span): AggregateQuery => ({
  ...range(span),
  filters: PAGE_VIEWS,
  metrics: [{ name: 'visitors', op: 'visitors' }],
});

/* ── the queries ─────────────────────────────────────────────────────────────── */

/** Page views, visitors and visits per address (their sum is the brand's total). */
export const totalsByHostQuery = (span: Span): AggregateQuery => ({
  ...range(span),
  filters: PAGE_VIEWS,
  groupBy: ['website_id'],
  metrics: [
    { name: 'views', op: 'count' },
    { name: 'visitors', op: 'visitors' },
    { name: 'visits', op: 'sessions' },
  ],
  orderBy: { field: 'visitors', direction: 'desc' },
  limit: 50,
});

/** Time actually spent on the site, and the visits it was measured on. */
export const timeQuery = (span: Span): AggregateQuery => ({
  ...range(span),
  filters: ENGAGEMENT,
  metrics: [
    { name: 'active_ms', op: 'sum', field: ACTIVE_MS },
    { name: 'visits', op: 'sessions' },
  ],
});

/** Visitors and page views per hour / day. */
export const trendQuery = (span: Span, bucket: TimeBucket): AggregateQuery => ({
  ...range(span),
  filters: PAGE_VIEWS,
  timeBucket: bucket,
  metrics: [
    { name: 'visitors', op: 'visitors' },
    { name: 'views', op: 'count' },
  ],
  orderBy: { field: 'time_bucket', direction: 'asc' },
  limit: 1000,
});

/** Visitors per UTC day across all the brand's addresses (a visitor counts once per address, as the totals count
 *  them). */
export const visitorsByDayQuery = (span: Span): AggregateQuery => ({
  ...range(span),
  filters: PAGE_VIEWS,
  timeBucket: 'day',
  metrics: [{ name: 'visitors', op: 'visitors' }],
  orderBy: { field: 'time_bucket', direction: 'asc' },
  limit: 1000,
});

/** The most viewed pages. */
export const topPagesQuery = (span: Span): AggregateQuery => ({
  ...range(span),
  filters: [...PAGE_VIEWS, { field: PATH, op: 'exists' }],
  groupBy: [PATH],
  metrics: [
    { name: 'views', op: 'count' },
    { name: 'visitors', op: 'visitors' },
  ],
  orderBy: { field: 'views', direction: 'desc' },
  limit: TOP_PAGES,
});

/** The pages people viewed most, for the page picker: optionally only those whose path contains `search` (the
 *  analytics service matches it ignoring case). */
export const pageListQuery = (span: Span, search: string, limit: number): AggregateQuery => {
  const needle = search.trim().slice(0, 100);
  return {
    ...range(span),
    filters: [
      ...PAGE_VIEWS,
      { field: PATH, op: 'exists' },
      ...(needle ? [{ field: PATH, op: 'contains' as const, value: needle }] : []),
    ],
    groupBy: [PATH],
    metrics: [{ name: 'views', op: 'count' }],
    orderBy: { field: 'views', direction: 'desc' },
    limit,
  };
};

/** Views per page path, from a page-list answer. */
export const viewsByPath = (rows: AnalyticsRow[]): { path: string; views: number }[] =>
  rows.map((r) => ({ path: str(r[PATH]), views: num(r.views) })).filter((p) => p.path !== '');

/** Views of each of the given pages (the period before, to compare the most visited pages with). */
export const pageViewsQuery = (span: Span, paths: string[], limit: number = TOP_PAGES): AggregateQuery => ({
  ...range(span),
  filters: [...PAGE_VIEWS, { field: PATH, op: 'in', value: paths }],
  groupBy: [PATH],
  metrics: [{ name: 'views', op: 'count' }],
  limit,
});

/** Time spent on each of the given pages. */
export const pageTimeQuery = (span: Span, paths: string[]): AggregateQuery => ({
  ...range(span),
  filters: [...ENGAGEMENT, { field: PATH, op: 'in', value: paths }],
  groupBy: [PATH],
  metrics: [{ name: 'active_ms', op: 'sum', field: ACTIVE_MS }],
  limit: TOP_PAGES,
});

/** Visitors per kind of device. */
export const devicesQuery = (span: Span): AggregateQuery => ({
  ...range(span),
  filters: PAGE_VIEWS,
  groupBy: [DEVICE],
  metrics: [{ name: 'visitors', op: 'visitors' }],
  orderBy: { field: 'visitors', direction: 'desc' },
  limit: 10,
});

/** Visitors per operating system. Pulsar names only iOS, Android, macOS and Windows; anything else (Linux,
 *  ChromeOS, a robot) arrives empty. */
export const systemsQuery = (span: Span): AggregateQuery => ({
  ...range(span),
  filters: PAGE_VIEWS,
  groupBy: [SYSTEM],
  metrics: [{ name: 'visitors', op: 'visitors' }],
  orderBy: { field: 'visitors', direction: 'desc' },
  limit: 10,
});

/* ── shaping the answers ─────────────────────────────────────────────────────── */

/** A count or sum as a number; counts arrive as decimal strings, an empty answer as null. */
export const num = (v: AnalyticsRow[string] | undefined): number => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

export const str = (v: AnalyticsRow[string] | undefined): string => (typeof v === 'string' ? v : '');

export type Totals = { views: number; visitors: number; visits: number };

export const sumTotals = (rows: AnalyticsRow[]): Totals =>
  rows.reduce<Totals>(
    (acc, r) => ({
      views: acc.views + num(r.views),
      visitors: acc.visitors + num(r.visitors),
      visits: acc.visits + num(r.visits),
    }),
    { views: 0, visitors: 0, visits: 0 },
  );

/** Average seconds per measured visit, or null when nothing was measured. */
export const avgSecondsPerVisit = (rows: AnalyticsRow[]): number | null => {
  const row = rows[0];
  if (!row) return null;
  const visits = num(row.visits);
  return visits > 0 ? num(row.active_ms) / visits / 1000 : null;
};

export type TrendPoint = { at: number; visitors: number; views: number };

/** The trend rows laid onto every bucket of the period (an hour / day with no visits reads 0). */
// Bucket times come with microseconds ("…T00:00:00.000000Z"); trim to the milliseconds Date.parse reads.
const parseBucket = (s: string): number => Date.parse(s.replace(/(\.\d{3})\d+/, '$1'));

export const fillTrend = (rows: AnalyticsRow[], buckets: number[]): TrendPoint[] => {
  const byStart = new Map<number, AnalyticsRow>();
  rows.forEach((r) => {
    const at = parseBucket(str(r.time_bucket));
    if (Number.isFinite(at)) byStart.set(at, r);
  });
  return buckets.map((at) => {
    const r = byStart.get(at);
    return { at, visitors: r ? num(r.visitors) : 0, views: r ? num(r.views) : 0 };
  });
};

/** One UTC day's visits (`at`: the day's start). */
export type DayVisitors = { at: number; visitors: number };

/** The days a {@link visitorsByDayQuery} answered (days without visitors are absent). */
export const dayVisitors = (rows: AnalyticsRow[]): DayVisitors[] =>
  rows
    .map((r) => ({ at: parseBucket(str(r.time_bucket)), visitors: num(r.visitors) }))
    .filter((d) => Number.isFinite(d.at));

/**
 * Daily visitors summed onto a chart's buckets (each bucket runs from its start to the next one's, the last to the end of
 * the period): Home's days, or its months on the longer ranges. A day before the first bucket counts nowhere. Pure.
 */
export const visitorsOnBuckets = (days: DayVisitors[], bucketStarts: number[]): number[] => {
  const out = bucketStarts.map(() => 0);
  days.forEach((d) => {
    let i = -1;
    bucketStarts.forEach((start, j) => {
      if (start <= d.at) i = j;
    });
    if (i >= 0) out[i] += d.visitors;
  });
  return out;
};

/** Visitors credited to one platform of one source (`platform` '' when the visit named none). */
export type SourceRow = { channel: Channel; platform: string; visitors: number };

export const shapeSources = (rows: AnalyticsRow[]): SourceRow[] =>
  rows
    .map((r) => ({ channel: str(r[CHANNEL_FIELD]), platform: str(r[PLATFORM_FIELD]), visitors: num(r.visitors) }))
    .filter((s): s is SourceRow => isChannel(s.channel) && s.visitors > 0);

export type ChannelRow = { channel: Channel; visitors: number };

/** Each source's and platform's arrivals (see {@link sourceReachQuery}). */
export type SourceReach = { channels: ChannelRow[]; platforms: SourceRow[] };

export const shapeSourceReach = (platformRows: AnalyticsRow[], channelRows: AnalyticsRow[]): SourceReach => ({
  platforms: platformRows
    .map((r) => ({
      channel: str(r[ARRIVAL_CHANNEL_FIELD]),
      platform: str(r[ARRIVAL_PLATFORM_FIELD]),
      visitors: num(r.visitors),
    }))
    .filter((s): s is SourceRow => isChannel(s.channel) && s.visitors > 0),
  channels: channelRows
    .map((r) => ({ channel: str(r[ARRIVAL_CHANNEL_FIELD]), visitors: num(r.visitors) }))
    .filter((c): c is ChannelRow => isChannel(c.channel) && c.visitors > 0),
});

/** Visitors per source, most first: the sum of its platforms'. Pure. */
export const channelTotals = (rows: SourceRow[]): ChannelRow[] => {
  const acc = new Map<Channel, number>();
  rows.forEach((r) => acc.set(r.channel, (acc.get(r.channel) ?? 0) + r.visitors));
  return [...acc.entries()]
    .map(([channel, visitors]) => ({ channel, visitors }))
    .sort((a, b) => b.visitors - a.visitors);
};

/** Visitors per link `aff_id` ('' for the visitors no link brought). */
export type LinkRow = { affId: string; visitors: number };

export const shapeLinks = (rows: AnalyticsRow[]): LinkRow[] =>
  rows.map((r) => ({ affId: str(r[LINK_FIELD]), visitors: num(r.visitors) })).filter((l) => l.visitors > 0);

/** A {@link linkReachQuery} answer: visitors per link they arrived through. */
export const shapeLinkReach = (rows: AnalyticsRow[]): LinkRow[] =>
  rows
    .map((r) => ({ affId: str(r[ARRIVAL_LINK_FIELD]), visitors: num(r.visitors) }))
    .filter((l) => l.affId !== '' && l.visitors > 0);

export type PageRow = {
  path: string;
  views: number;
  visitors: number;
  avgSeconds: number | null;
  /** The page's views in the period before (null: not read, so no comparison). */
  previousViews: number | null;
};

export const shapePages = (rows: AnalyticsRow[], timeRows: AnalyticsRow[]): PageRow[] => {
  const timeByPath = new Map<string, number>();
  timeRows.forEach((r) => timeByPath.set(str(r[PATH]), num(r.active_ms)));
  return rows
    .map((r) => {
      const path = str(r[PATH]);
      const views = num(r.views);
      const active = timeByPath.get(path);
      return {
        path,
        views,
        visitors: num(r.visitors),
        avgSeconds: active !== undefined && views > 0 ? active / views / 1000 : null,
        previousViews: null,
      };
    })
    .filter((p) => p.path !== '');
};

export const pagePaths = (rows: AnalyticsRow[]): string[] => rows.map((r) => str(r[PATH])).filter((p) => p !== '');

export type DeviceKind = 'mobile' | 'desktop' | 'tablet' | 'other';
export type DeviceRow = { kind: DeviceKind; visitors: number };

export const shapeDevices = (rows: AnalyticsRow[]): DeviceRow[] => {
  const acc = new Map<DeviceKind, number>();
  rows.forEach((r) => {
    const raw = str(r[DEVICE]).toLowerCase();
    const kind: DeviceKind = raw === 'mobile' || raw === 'desktop' || raw === 'tablet' ? raw : 'other';
    acc.set(kind, (acc.get(kind) ?? 0) + num(r.visitors));
  });
  return [...acc.entries()]
    .map(([kind, visitors]) => ({ kind, visitors }))
    .filter((d) => d.visitors > 0)
    .sort((a, b) => b.visitors - a.visitors);
};

export type SystemKind = 'ios' | 'android' | 'windows' | 'macos' | 'other';
export type SystemRow = { kind: SystemKind; visitors: number };

const SYSTEMS: Record<string, SystemKind> = { ios: 'ios', android: 'android', windows: 'windows', macos: 'macos' };

export const shapeSystems = (rows: AnalyticsRow[]): SystemRow[] => {
  const acc = new Map<SystemKind, number>();
  rows.forEach((r) => {
    const kind = SYSTEMS[str(r[SYSTEM]).toLowerCase()] ?? 'other';
    acc.set(kind, (acc.get(kind) ?? 0) + num(r.visitors));
  });
  return (
    [...acc.entries()]
      .map(([kind, visitors]) => ({ kind, visitors }))
      .filter((d) => d.visitors > 0)
      // "Other" is what is left over, so it comes last whatever its size.
      .sort((a, b) => Number(a.kind === 'other') - Number(b.kind === 'other') || b.visitors - a.visitors)
  );
};

export type HostRow = { host: string; visitors: number; views: number };

export const shapeHosts = (rows: AnalyticsRow[]): HostRow[] =>
  rows
    .map((r) => ({ host: str(r.website_id), visitors: num(r.visitors), views: num(r.views) }))
    .filter((h) => h.host !== '');
